import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import {
  EMOJI_CATEGORIES,
  getRecentEmojis,
  pushRecentEmoji,
  searchEmojis,
} from "../../../utils/whatsappEmojis";

const DEFAULT_PREVIEW = { emoji: "😊", label: "What's Your Mood?" };

export default function ChatEmojiPicker({ open, onClose, onSelect, anchorRef }) {
  const panelRef = useRef(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("smileys");
  const [preview, setPreview] = useState(DEFAULT_PREVIEW);
  const [recent, setRecent] = useState(() => getRecentEmojis());

  const searchResults = useMemo(() => searchEmojis(search), [search]);

  const isSearching = search.trim().length > 0;

  const activeEmojis = useMemo(() => {
    if (isSearching) return searchResults ?? [];
    if (activeCategory === "recent") {
      return recent.length ? recent : EMOJI_CATEGORIES[0].emojis.slice(0, 36);
    }
    const cat = EMOJI_CATEGORIES.find((c) => c.id === activeCategory);
    return cat?.emojis || [];
  }, [activeCategory, recent, isSearching, searchResults]);

  const activeLabel = useMemo(() => {
    if (isSearching) return "Search results";
    if (activeCategory === "recent") return "Frequently Used";
    return EMOJI_CATEGORIES.find((c) => c.id === activeCategory)?.label || "";
  }, [activeCategory, isSearching]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setPreview(DEFAULT_PREVIEW);
      return undefined;
    }
    setRecent(getRecentEmojis());

    const handlePointerDown = (event) => {
      const panel = panelRef.current;
      const anchor = anchorRef?.current;
      if (panel?.contains(event.target) || anchor?.contains(event.target)) {
        return;
      }
      onClose?.();
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const handlePick = (emoji) => {
    pushRecentEmoji(emoji);
    setRecent(getRecentEmojis());
    onSelect?.(emoji);
  };

  return (
    <div className="wa-emoji-picker" ref={panelRef} role="dialog" aria-label="Emoji picker">
      <div className="wa-emoji-picker-search-row">
        <div className="wa-emoji-picker-search">
          <Icon icon="solar:magnifer-linear" width={16} />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label="Search emojis"
          />
        </div>
        <span className="wa-emoji-skin-tone" title="Skin tone" aria-hidden>
          🟨
        </span>
      </div>

      <div className="wa-emoji-categories" role="tablist">
        <button
          type="button"
          role="tab"
          className={`wa-emoji-cat-btn ${activeCategory === "recent" ? "active" : ""}`}
          title="Frequently used"
          onClick={() => {
            setSearch("");
            setActiveCategory("recent");
          }}
        >
          <Icon icon="solar:clock-circle-linear" width={18} />
        </button>
        {EMOJI_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            role="tab"
            className={`wa-emoji-cat-btn ${activeCategory === cat.id ? "active" : ""}`}
            title={cat.label}
            onClick={() => {
              setSearch("");
              setActiveCategory(cat.id);
            }}
          >
            <Icon icon={cat.icon} width={18} />
          </button>
        ))}
      </div>

      <div className="wa-emoji-section-label">{activeLabel}</div>

      <div className="wa-emoji-grid">
        {activeEmojis.length === 0 ? (
          <div className="wa-emoji-empty">No emojis found</div>
        ) : (
          activeEmojis.map((emoji, idx) => (
            <button
              key={`${emoji}-${idx}`}
              type="button"
              className="wa-emoji-btn"
              onMouseEnter={() =>
                setPreview({ emoji, label: activeLabel || "Emoji" })
              }
              onFocus={() =>
                setPreview({ emoji, label: activeLabel || "Emoji" })
              }
              onClick={() => handlePick(emoji)}
              aria-label={`Insert ${emoji}`}
            >
              {emoji}
            </button>
          ))
        )}
      </div>

      <div className="wa-emoji-footer">
        <span className="wa-emoji-footer-preview" aria-hidden>
          {preview.emoji}
        </span>
        <span className="wa-emoji-footer-label">{preview.label}</span>
      </div>
    </div>
  );
}
